import { Flex, Skeleton, Typography } from "antd";
import ProfileMenuCard from "./ProfileMenuCard";
import { GrSettingsOption } from "react-icons/gr";
import { GoShieldLock } from "react-icons/go";
import { TiMessages } from "react-icons/ti";
import { PiNotepadFill } from "react-icons/pi";
import { HiDevicePhoneMobile } from "react-icons/hi2";
import {
  useAuthUser,
  useCustomerManagementStore,
  useModal,
  useSupport,
} from "_hooks";
import { AppLogoutIcon } from "_lib/AppIcons";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import { LEGAL_DOCUMENTS_URL, SETTINGS_ROUTE } from "_constants";
import {
  StyledProfileMenuUserCard,
  StyledProfileMenuUserInfo,
} from "./index.styled";
import AppAvatar from "_lib/AppAvatar";
import ProfileAddSecondaryNumberModal from "../ProfileAddSecondaryNumberModal";
import { getPrimaryAndSecondaryPhoneNumber } from "_helpers";

const ProfileMenu = () => {
  const { user } = useAuthUser();
  const {
    customerManagement: { userStatus },
    logout,
    fetchUser,
  } = useCustomerManagementStore();

  const navigate = useNavigate();
  const { openSupport } = useSupport();
  const { open, toggle } = useModal();

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const onRedirect = useCallback(
    (url) => () => {
      if (url.includes("https")) {
        window.open(url, "_blank");
      } else {
        navigate(url);
      }
    },
    [navigate]
  );

  const { primaryPhoneNumberContact, secondaryPhoneNumberContact } = useMemo(
    () => getPrimaryAndSecondaryPhoneNumber(user?.contacts),
    [user?.contacts]
  );

  const _secondProfileMenus = useMemo(() => {
    return secondProfileMenus.map((profileMenu) => {
      let newProfileMenu = { ...profileMenu };
      if (newProfileMenu?.path) {
        newProfileMenu.onClick = onRedirect(newProfileMenu.path);
      }
      return newProfileMenu;
    });
  }, [onRedirect]);

  const userLoading = userStatus === "loading";

  return (
    <>
      <Flex vertical gap={16} justify="flex-start">
        <Flex gap={8} align="center" justify="space-between">
          <StyledProfileMenuUserCard gap={16} align="center">
            {userLoading ? (
              <Skeleton active />
            ) : (
              <>
                <AppAvatar />
                <StyledProfileMenuUserInfo vertical justify="space-between">
                  <Text strong ellipsis>
                    {user?.fullName}
                  </Text>
                  <Text type="secondary" ellipsis>
                    <b>Mobile:</b> {primaryPhoneNumberContact?.contact}
                  </Text>
                  {secondaryPhoneNumberContact && (
                    <Text type="secondary" ellipsis>
                      <b>Secondary:</b> {secondaryPhoneNumberContact?.contact}
                    </Text>
                  )}
                  <Text type="secondary" ellipsis>
                    <b>Email:</b> {user?.email}
                  </Text>
                  <Text type="secondary" ellipsis>
                    <b>Country:</b> {user?.country}
                  </Text>
                </StyledProfileMenuUserInfo>
              </>
            )}
          </StyledProfileMenuUserCard>
        </Flex>

        {!secondaryPhoneNumberContact && (
          <ProfileMenuCard
            items={[
              {
                icon: <HiDevicePhoneMobile size={24} />,
                title: "Add Secondary Number",
                subtitle: "Use an extra number to transact more",
                onClick: toggle,
              },
            ]}
          />
        )}

        <ProfileMenuCard
          items={[
            {
              icon: <GrSettingsOption size={24} />,
              title: "Settings",
              subtitle: "Manage your account",
              onClick: onRedirect(SETTINGS_ROUTE),
            },
          ]}
        />
        <ProfileMenuCard items={_secondProfileMenus} />
        <ProfileMenuCard
          items={[
            {
              icon: <TiMessages size={24} />,
              title: "Live Chat",
              subtitle: "Feel free to contact our customer support",
              onClick: openSupport,
            },
          ]}
        />
        <ProfileMenuCard
          items={[
            {
              icon: <AppLogoutIcon isActionable={false} />,
              title: "Logout",
              subtitle: "We are sad to see you go 😔",
              onClick: logout,
            },
          ]}
        />
      </Flex>
      <ProfileAddSecondaryNumberModal open={open} toggle={toggle} />
    </>
  );
};

const secondProfileMenus = [
  {
    icon: <GoShieldLock size={24} />,
    title: "Help Center",
    subtitle: "Find answers to your questions",
  },
  {
    icon: <PiNotepadFill size={24} />,
    title: "Legal Documents",
    subtitle: "EmpireFX Kenya Limited",
    path: LEGAL_DOCUMENTS_URL,
  },
];

const { Text } = Typography;
export default ProfileMenu;
